/**
 * Conversation Intelligence Dashboard
 *
 * Comprehensive view of the Category Conversation Intelligence System.
 * Shows harvested conversations, participation opportunities, sentiment trends,
 * entity mentions, and authorized voices (expert candidates).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  MessageSquare,
  TrendingUp,
  Users,
  Target,
  HelpCircle,
  BarChart3,
  RefreshCw,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Play,
  Search,
  Globe,
  Youtube,
  MessageCircle,
  Star,
  Mail,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Sparkles,
  DollarSign,
  UserPlus,
  Link,
  Unlink,
  Mic
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

import {
  fetchConversations,
  fetchOpportunities,
  fetchSentimentTrends,
  fetchEntities,
  fetchAuthorizedVoices,
  fetchExpertStats,
  fetchOutreachPipeline,
  triggerHarvest,
  updateVoiceOutreach,
  fetchHarvestJob,
  fetchEntityMentions,
  fetchVoiceDetails,
  fetchExpertProfileSummary,
  fetchMarketStats,
  linkVoiceToExpert,
  unlinkVoiceFromExpert,
  createExpertFromVoice,
  fetchMatchingExperts,
  Conversation,
  ParticipationOpportunity,
  SentimentTrend,
  EntityMention,
  AuthorizedVoice,
  ExpertStats,
  OutreachPipeline,
  ExtractedQuestion,
  EntityMentionsResponse,
  ExpertProfileSummary,
  MarketStats,
  PLATFORM_CONFIG,
  OUTREACH_STATUS_CONFIG,
  SENTIMENT_CONFIG
} from '@/services/conversationIntelligenceService';
import type { ExpertConversation } from '@/services/conversationIntelligenceService';
import { authFetch } from '@/utils/auth-fetch';

const SENTIMENT_COLORS = {
  positive: '#22c55e',
  neutral: '#6b7280',
  negative: '#ef4444',
  mixed: '#eab308'
};

export default function ConversationIntelligenceDashboard() {
  const { selectedSite } = useSite();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [opportunities, setOpportunities] = useState<ParticipationOpportunity[]>([]);
  const [sentimentTrends, setSentimentTrends] = useState<SentimentTrend[]>([]);
  const [sentimentSummary, setSentimentSummary] = useState<{ totalConversations: number; avgSentimentScore: number; positivePercent: string; negativePercent: string } | null>(null);
  const [entities, setEntities] = useState<EntityMention[]>([]);
  const [experts, setExperts] = useState<AuthorizedVoice[]>([]);
  const [expertStats, setExpertStats] = useState<ExpertStats | null>(null);
  const [pipeline, setPipeline] = useState<OutreachPipeline | null>(null);
  const [extractedQuestions, setExtractedQuestions] = useState<ExtractedQuestion[]>([]);

  // Filter states
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [sentimentFilter, setSentimentFilter] = useState<string>('all');

  // Harvest state
  const [isHarvesting, setIsHarvesting] = useState(false);
  const [harvestPopoverOpen, setHarvestPopoverOpen] = useState(false);

  // Entity detail sheet state
  const [selectedEntity, setSelectedEntity] = useState<{ name: string; isCompetitor: boolean } | null>(null);
  const [entityMentions, setEntityMentions] = useState<EntityMentionsResponse | null>(null);
  const [loadingEntityMentions, setLoadingEntityMentions] = useState(false);

  // Expert detail sheet state
  const [selectedExpert, setSelectedExpert] = useState<AuthorizedVoice | null>(null);
  const [expertConversations, setExpertConversations] = useState<ExpertConversation[]>([]);
  const [expertProfileSummary, setExpertProfileSummary] = useState<ExpertProfileSummary | null>(null);
  const [loadingExpertProfile, setLoadingExpertProfile] = useState(false);

  // Expert voice linking state
  const [linkingVoiceId, setLinkingVoiceId] = useState<string | null>(null);
  const [matchingExpertsForLink, setMatchingExpertsForLink] = useState<Array<{ id: string; name: string; email?: string; role?: string }>>([]);
  const [loadingMatchingExperts, setLoadingMatchingExperts] = useState(false);
  const [creatingExpert, setCreatingExpert] = useState(false);
  const [createExpertOverrides, setCreateExpertOverrides] = useState<{ name: string; role: string; bio: string; email: string }>({ name: '', role: '', bio: '', email: '' });
  const [showCreateExpertDialog, setShowCreateExpertDialog] = useState(false);
  const [showLinkExpertDialog, setShowLinkExpertDialog] = useState(false);
  const [linkingError, setLinkingError] = useState<string | null>(null);

  // Get sector ID from site (with fallback)
  const sectorId = selectedSite?.sector_id || (selectedSite?.sector_ids?.[0]) || null;
  const categoryId = null; // null means all categories

  // Category selector state
  const [siteCategories, setSiteCategories] = useState<Array<{ id: number; category_name: string; category_path: string; product_count: number }>>([]);
  const [selectedCategoryPath, setSelectedCategoryPath] = useState<string>('');
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Market selector state
  const [selectedMarket, setSelectedMarket] = useState<string>('en');
  const [marketStats, setMarketStats] = useState<MarketStats | null>(null);
  const MARKET_OPTIONS = [
    { value: 'en', label: 'English (US)' },
    { value: 'en-gb', label: 'English (UK)' },
    { value: 'es', label: 'Spanish (Spain)' },
    { value: 'es-mx', label: 'Spanish (Mexico)' },
    { value: 'de', label: 'German' },
    { value: 'fr', label: 'French' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'pt-br', label: 'Portuguese (Brazil)' },
    { value: 'nl', label: 'Dutch' }
  ];

  // Get count for selected market
  const getMarketCount = (market: string): number => {
    if (!marketStats) return 0;
    const found = marketStats.markets.find(m => m.market === market);
    return found ? parseInt(found.count) : 0;
  };

  // Navigate to Discovery with a pre-populated prompt for generating a platform reply
  const openDiscoveryForReply = (
    question: string,
    platform: string,
    conversationTitle: string,
    conversationUrl: string,
    conversationText?: string,
    suggestedApproach?: string,
    questionType?: string // 'how-to' | 'comparison' | 'recommendation' | 'troubleshooting' | 'general'
  ) => {
    // Build a prompt optimized for the platform
    const platformHints: Record<string, string> = {
      reddit: 'Keep it conversational, helpful, and authentic. Reddit users appreciate genuine advice without being salesy.',
      youtube: 'Keep it concise for a YouTube comment. Be helpful and direct.',
      forum: 'Provide detailed, expert-level advice. Forum users appreciate thorough explanations.',
      twitter: 'Keep it brief and to the point (280 chars). Be helpful.'
    };

    const hint = platformHints[platform] || platformHints.forum;

    // Create a summary of the conversation context (truncate if too long)
    const contextSummary = conversationText
      ? conversationText.substring(0, 1500) + (conversationText.length > 1500 ? '...' : '')
      : '';

    // Determine if this is a product recommendation question or informational
    const isProductQuestion = questionType === 'recommendation' || questionType === 'comparison';

    let prompt: string;

    if (isProductQuestion) {
      // Product recommendation - search catalog and suggest specific products
      prompt = `Search our product catalog and write a helpful ${platform} reply for this conversation.

**Conversation Title:** "${conversationTitle}"

**What they're asking:**
${contextSummary || question}

${suggestedApproach ? `**Approach:** ${suggestedApproach}` : ''}

Find 2-3 relevant products from our catalog that match their needs. Then write a natural reply that:
- Recommends specific products by name with brief reasons why
- Addresses their question directly
- Uses ${hint.toLowerCase()}
- Is ready to copy-paste (no intro like "Here's a reply")`;
    } else {
      // Informational question - provide helpful advice, only mention products if truly relevant
      prompt = `Write a helpful ${platform} reply for this conversation.

**Conversation Title:** "${conversationTitle}"

**What they're asking:**
${contextSummary || question}

${suggestedApproach ? `**Approach:** ${suggestedApproach}` : ''}

Write a natural, helpful reply that:
- Provides clear, practical advice to answer their question
- Only mentions products if directly relevant to solving their problem
- Uses ${hint.toLowerCase()}
- Is ready to copy-paste (no intro like "Here's a reply")`;
    }

    navigate('/discovery', {
      state: {
        autoPrompt: prompt,
        source: 'conversation-intelligence',
        platform,
        conversationUrl
      }
    });
  };

  // Load site categories when site changes
  useEffect(() => {
    if (selectedSite) {
      loadCategories();
      loadData();
    }
  }, [selectedSite, selectedMarket]);

  const loadCategories = async () => {
    if (!selectedSite) return;
    setLoadingCategories(true);
    try {
      const response = await authFetch(`/api/sites/${selectedSite.id}/categories?flat=true`);
      if (response.ok) {
        const data = await response.json();
        setSiteCategories(data.categories || []);
        // Auto-select first category if none selected
        if (data.categories?.length > 0 && !selectedCategoryPath) {
          const firstCat = data.categories[0];
          setSelectedCategoryPath(firstCat.category_path || firstCat.category_name);
        }
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadMarketStats = async () => {
    if (!selectedSite) return;
    try {
      const stats = await fetchMarketStats(selectedSite.id);
      setMarketStats(stats);
    } catch (err) {
      console.error('Failed to load market stats:', err);
    }
  };

  // Load market stats when site changes
  useEffect(() => {
    if (selectedSite) {
      loadMarketStats();
    }
  }, [selectedSite]);

  const loadData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      // Load all data in parallel
      const [
        conversationsRes,
        opportunitiesRes,
        sentimentRes,
        entitiesRes,
        expertsRes,
        statsRes,
        pipelineRes
      ] = await Promise.allSettled([
        fetchConversations(selectedSite.id, { limit: 50, market: selectedMarket }),
        fetchOpportunities(selectedSite.id, { limit: 20, market: selectedMarket }),
        fetchSentimentTrends(selectedSite.id, categoryId, 'weekly', 12),
        fetchEntities(selectedSite.id, { limit: 50 }),
        sectorId ? fetchAuthorizedVoices(sectorId, { limit: 50 }) : Promise.resolve({ experts: [] }),
        sectorId ? fetchExpertStats(sectorId) : Promise.resolve({ stats: null }),
        sectorId ? fetchOutreachPipeline(sectorId) : Promise.resolve({ pipeline: null })
      ]);

      if (conversationsRes.status === 'fulfilled') {
        const convs = conversationsRes.value.conversations;
        setConversations(convs);

        // Extract questions from conversations
        const questions: ExtractedQuestion[] = [];
        for (const conv of convs) {
          if ((conv as any).questions && Array.isArray((conv as any).questions)) {
            for (const q of (conv as any).questions) {
              questions.push({
                question: q.question,
                type: q.type || 'general',
                answered: q.answered || false,
                conversationId: conv.id,
                platform: conv.platform,
                conversationUrl: conv.conversation_url,
                conversationTitle: conv.conversation_title || '',
                sentiment: conv.sentiment,
                postedAt: conv.posted_at,
                upvotes: conv.upvotes
              });
            }
          }
        }
        setExtractedQuestions(questions);

        // Calculate sentiment summary from actual conversations
        const withSentiment = convs.filter(c => c.sentiment);
        if (withSentiment.length > 0) {
          const positive = withSentiment.filter(c => c.sentiment === 'positive').length;
          const negative = withSentiment.filter(c => c.sentiment === 'negative').length;
          const neutral = withSentiment.filter(c => c.sentiment === 'neutral').length;
          const total = withSentiment.length;
          setSentimentSummary({
            totalConversations: total,
            avgSentimentScore: 0,
            positivePercent: ((positive / total) * 100).toFixed(1),
            negativePercent: ((negative / total) * 100).toFixed(1)
          });
        }
      }
      if (opportunitiesRes.status === 'fulfilled') {
        setOpportunities(opportunitiesRes.value.opportunities);
      }
      if (sentimentRes.status === 'fulfilled') {
        setSentimentTrends(sentimentRes.value.trends);
        // Only use API sentiment summary if it has data, otherwise keep the calculated one
        if (sentimentRes.value.summary && sentimentRes.value.summary.totalConversations > 0) {
          setSentimentSummary(sentimentRes.value.summary);
        }
      }
      if (entitiesRes.status === 'fulfilled') {
        setEntities(entitiesRes.value.entities);
      }
      if (expertsRes.status === 'fulfilled') {
        setExperts(expertsRes.value.experts);
      }
      if (statsRes.status === 'fulfilled') {
        setExpertStats(statsRes.value.stats);
      }
      if (pipelineRes.status === 'fulfilled') {
        setPipeline(pipelineRes.value.pipeline);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const [harvestJobId, setHarvestJobId] = useState<string | null>(null);
  const [harvestProgress, setHarvestProgress] = useState<string>('');

  const handleHarvest = async () => {
    if (!selectedSite) return;

    if (!selectedCategoryPath) {
      setError('Please select a category to harvest conversations for');
      return;
    }

    setIsHarvesting(true);
    const marketLabel = MARKET_OPTIONS.find(m => m.value === selectedMarket)?.label || selectedMarket;
    setHarvestProgress(`Starting harvest for "${selectedCategoryPath}" (${marketLabel})...`);
    setError(null);

    try {
      // Trigger harvest - returns immediately with job ID
      const response = await triggerHarvest(selectedSite.id, selectedCategoryPath, {
        platforms: ['reddit', 'youtube', 'forum'],
        maxResults: 20,
        market: selectedMarket
      });

      const jobId = response.jobId;
      setHarvestJobId(jobId);
      setHarvestProgress('Harvesting conversations from Reddit, YouTube, forums...');

      // Poll for job status
      let attempts = 0;
      const maxAttempts = 120; // 10 minutes max (5 sec intervals)

      const pollStatus = async () => {
        try {
          const status = await fetchHarvestJob(jobId);

          if (status.job?.status === 'completed') {
            setHarvestProgress(`Completed! Found ${status.job.total_new || 0} new conversations.`);
            setIsHarvesting(false);
            setHarvestJobId(null);
            // Reload data
            await loadData();
            return;
          }

          if (status.job?.status === 'failed') {
            setError(`Harvest failed: ${status.job.error_message || 'Unknown error'}`);
            setIsHarvesting(false);
            setHarvestJobId(null);
            return;
          }

          // Still running - update progress
          if (status.job?.status === 'running') {
            const discovered = status.job.total_discovered || 0;
            const newCount = status.job.total_new || 0;
            setHarvestProgress(`Harvesting... Found ${discovered} conversations (${newCount} new)`);
          }

          // Continue polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 5000); // Poll every 5 seconds
          } else {
            setHarvestProgress('Harvest is taking longer than expected. Check back later.');
            setIsHarvesting(false);
          }
        } catch (pollError) {
          console.error('Error polling harvest status:', pollError);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 5000);
          }
        }
      };

      // Start polling after a short delay
      setTimeout(pollStatus, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start harvest');
      setIsHarvesting(false);
      setHarvestProgress('');
    }
  };

  const handleOutreachUpdate = async (voiceId: string, newStatus: string) => {
    try {
      await updateVoiceOutreach(voiceId, newStatus);
      // Reload experts
      const expertsRes = await fetchAuthorizedVoices(sectorId, { limit: 50 });
      setExperts(expertsRes.experts);
      const pipelineRes = await fetchOutreachPipeline(sectorId);
      setPipeline(pipelineRes.pipeline);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  // Handle entity click - show what they're saying
  const handleEntityClick = async (entityName: string, isCompetitor: boolean) => {
    if (!selectedSite?.id) return;

    setSelectedEntity({ name: entityName, isCompetitor });
    setLoadingEntityMentions(true);
    setEntityMentions(null);

    try {
      const response = await fetchEntityMentions(selectedSite.id, entityName, 20);
      setEntityMentions(response);
    } catch (err) {
      console.error('Failed to fetch entity mentions:', err);
    } finally {
      setLoadingEntityMentions(false);
    }
  };

  // Handle expert click - show profile with AI summary
  const handleExpertClick = async (expert: AuthorizedVoice) => {
    setSelectedExpert(expert);
    setLoadingExpertProfile(true);
    setExpertConversations([]);
    setExpertProfileSummary(null);

    try {
      // Fetch details and AI summary in parallel
      const [detailsRes, summaryRes] = await Promise.all([
        fetchVoiceDetails(expert.id),
        fetchExpertProfileSummary(expert.id)
      ]);

      setExpertConversations(detailsRes.expert.recentConversations || []);
      setExpertProfileSummary(summaryRes.profileSummary);
    } catch (err) {
      console.error('Failed to fetch expert profile:', err);
    } finally {
      setLoadingExpertProfile(false);
    }
  };

  // Reload experts list
  const reloadExperts = async () => {
    if (!sectorId) return;
    try {
      const expertsRes = await fetchAuthorizedVoices(sectorId, { limit: 50 });
      setExperts(expertsRes.experts);
    } catch (err) {
      console.error('Failed to reload experts:', err);
    }
  };

  // Handle "Create Expert" from voice
  const handleCreateExpertFromVoice = async (voice: AuthorizedVoice) => {
    setShowCreateExpertDialog(true);
    setLinkingVoiceId(voice.id);
    setCreateExpertOverrides({
      name: voice.display_name || voice.username || '',
      role: '',
      bio: '',
      email: '',
    });
  };

  const submitCreateExpert = async () => {
    if (!linkingVoiceId || !selectedSite?.id) return;
    setCreatingExpert(true);
    setLinkingError(null);
    try {
      await createExpertFromVoice(linkingVoiceId, selectedSite.id, createExpertOverrides);
      setShowCreateExpertDialog(false);
      setLinkingVoiceId(null);
      await reloadExperts();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create expert';
      setLinkingError(msg);
      console.error('Failed to create expert from voice:', err);
    } finally {
      setCreatingExpert(false);
    }
  };

  // Handle "Link to Existing" flow
  const handleLinkToExisting = async (voice: AuthorizedVoice) => {
    if (!selectedSite?.id) return;
    setShowLinkExpertDialog(true);
    setLinkingVoiceId(voice.id);
    setLoadingMatchingExperts(true);
    setLinkingError(null);
    try {
      const res = await fetchMatchingExperts(voice.id, selectedSite.id);
      setMatchingExpertsForLink(res.experts || []);
    } catch (err) {
      console.error('Failed to fetch matching experts:', err);
      setMatchingExpertsForLink([]);
      setLinkingError(err instanceof Error ? err.message : 'Failed to fetch matching experts');
    } finally {
      setLoadingMatchingExperts(false);
    }
  };

  const submitLinkExpert = async (expertId: string) => {
    if (!linkingVoiceId) return;
    setLinkingError(null);
    try {
      await linkVoiceToExpert(linkingVoiceId, expertId);
      setShowLinkExpertDialog(false);
      setLinkingVoiceId(null);
      await reloadExperts();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to link expert';
      setLinkingError(msg);
      console.error('Failed to link voice to expert:', err);
    }
  };

  // Handle unlink
  const handleUnlinkExpert = async (voiceId: string) => {
    try {
      await unlinkVoiceFromExpert(voiceId);
      await reloadExperts();
      if (selectedExpert?.id === voiceId) {
        setSelectedExpert(prev => prev ? { ...prev, linked_expert_id: undefined, linked_expert_name: undefined } : null);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to unlink expert';
      setLinkingError(msg);
      console.error('Failed to unlink expert:', err);
    }
  };

  // Navigate to interview wizard with expert pre-selected
  const handleStartInterview = (expertId: string) => {
    navigate(`/content-interviews?expertId=${expertId}&approach=expert_matched`);
  };

  // Calculate overview metrics
  const overviewMetrics = {
    totalConversations: conversations.length,
    opportunities: opportunities.length,
    positivePercent: sentimentSummary?.positivePercent || '0',
    negativePercent: sentimentSummary?.negativePercent || '0',
    expertCandidates: expertStats?.total_experts || 0,
    activeCollaborations: expertStats?.active_collaborations || 0
  };

  // Helper to parse ISO week format (e.g., "2026-W07") or regular date
  const parseWeekDate = (dateStr: string): string => {
    // Check if it's ISO week format like "2026-W07"
    const weekMatch = dateStr.match(/^(\d{4})-W(\d{2})$/);
    if (weekMatch) {
      const year = parseInt(weekMatch[1]);
      const week = parseInt(weekMatch[2]);
      // Return formatted week string
      return `W${week} ${year}`;
    }
    // Try parsing as regular date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    // Fallback to original string
    return dateStr;
  };

  // Prepare chart data
  const sentimentChartData = sentimentTrends.map(t => ({
    date: parseWeekDate(t.period_start),
    positive: Number(t.positive_count) || 0,
    neutral: Number(t.neutral_count) || 0,
    negative: Number(t.negative_count) || 0,
    total: Number(t.total_conversations) || 0
  })).reverse();

  const platformDistribution = conversations.reduce((acc, c) => {
    acc[c.platform] = (acc[c.platform] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const platformChartData = Object.entries(platformDistribution).map(([name, value]) => ({
    name: PLATFORM_CONFIG[name]?.label || name,
    value,
    color: PLATFORM_CONFIG[name]?.color.replace('bg-', '') || 'gray-500'
  }));

  if (!selectedSite) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Please select a site to view conversation intelligence.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Conversation Intelligence</h1>
          <p className="text-muted-foreground mt-1">
            Monitor online conversations, find participation opportunities, and identify expert voices
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Market/Language Selector - controls data filtering globally */}
          <Select
            value={selectedMarket}
            onValueChange={setSelectedMarket}
          >
            <SelectTrigger className="w-[160px]">
              <Globe className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MARKET_OPTIONS.map((market) => (
                <SelectItem key={market.value} value={market.value}>
                  {market.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {/* Harvest button with popover for category selection */}
          <Popover open={harvestPopoverOpen} onOpenChange={setHarvestPopoverOpen}>
            <PopoverTrigger asChild>
              <Button disabled={isHarvesting}>
                <Play className={`h-4 w-4 mr-2 ${isHarvesting ? 'animate-pulse' : ''}`} />
                {isHarvesting ? 'Harvesting...' : 'Harvest Conversations'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-1">Harvest Conversations</h4>
                  <p className="text-sm text-muted-foreground">Select a product category to discover online conversations about.</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <Select
                    value={selectedCategoryPath}
                    onValueChange={setSelectedCategoryPath}
                    disabled={loadingCategories || siteCategories.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingCategories ? "Loading..." : "Select category"} />
                    </SelectTrigger>
                    <SelectContent>
                      {siteCategories.filter((cat) => cat.category_path || cat.category_name).map((cat) => (
                        <SelectItem
                          key={cat.id}
                          value={cat.category_path || cat.category_name}
                        >
                          {cat.category_name} ({cat.product_count} products)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  Market: <span className="font-medium text-foreground">{MARKET_OPTIONS.find(m => m.value === selectedMarket)?.label || selectedMarket}</span>
                </div>
                <Button
                  className="w-full"
                  onClick={() => {
                    setHarvestPopoverOpen(false);
                    handleHarvest();
                  }}
                  disabled={!selectedCategoryPath}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start Harvest
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!loadingCategories && siteCategories.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No product categories found for this site. Complete the AEO onboarding wizard to set up categories before harvesting conversations.
          </AlertDescription>
        </Alert>
      )}

      {isHarvesting && harvestProgress && (
        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
          <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
          <AlertDescription className="text-blue-700 dark:text-blue-300">
            {harvestProgress}
          </AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="flex items-center gap-1">
            <Target className="h-4 w-4" />
            Opportunities
          </TabsTrigger>
          <TabsTrigger value="sentiment" className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4" />
            Sentiment
          </TabsTrigger>
          <TabsTrigger value="questions" className="flex items-center gap-1">
            <HelpCircle className="h-4 w-4" />
            Questions
          </TabsTrigger>
          <TabsTrigger value="entities" className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            Mentions
          </TabsTrigger>
          <TabsTrigger value="experts" className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            Experts
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Conversations ({MARKET_OPTIONS.find(m => m.value === selectedMarket)?.label || selectedMarket})
                        </p>
                        <p className="text-3xl font-bold">{getMarketCount(selectedMarket)}</p>
                      </div>
                      <MessageSquare className="h-10 w-10 text-blue-500 opacity-20" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Participation Opportunities</p>
                        <p className="text-3xl font-bold text-green-600">{overviewMetrics.opportunities}</p>
                      </div>
                      <Target className="h-10 w-10 text-green-500 opacity-20" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Positive Sentiment</p>
                        <p className="text-3xl font-bold text-green-600">{overviewMetrics.positivePercent}%</p>
                      </div>
                      <ThumbsUp className="h-10 w-10 text-green-500 opacity-20" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Expert Candidates</p>
                        <p className="text-3xl font-bold text-purple-600">{overviewMetrics.expertCandidates}</p>
                        <p className="text-xs text-muted-foreground">{overviewMetrics.activeCollaborations} active</p>
                      </div>
                      <Users className="h-10 w-10 text-purple-500 opacity-20" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Sentiment Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle>Sentiment Over Time</CardTitle>
                    <CardDescription>Weekly conversation sentiment breakdown</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={sentimentChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="positive" stackId="a" fill={SENTIMENT_COLORS.positive} name="Positive" />
                        <Bar dataKey="neutral" stackId="a" fill={SENTIMENT_COLORS.neutral} name="Neutral" />
                        <Bar dataKey="negative" stackId="a" fill={SENTIMENT_COLORS.negative} name="Negative" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Platform Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle>Conversations by Platform</CardTitle>
                    <CardDescription>Distribution across online communities</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={platformChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {platformChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={['#f97316', '#ef4444', '#3b82f6', '#0ea5e9'][index % 4]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Opportunities Preview */}
              <Card>
                <CardHeader>
                  <CardTitle>Latest Participation Opportunities</CardTitle>
                  <CardDescription>High-value conversations where you can engage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {opportunities.slice(0, 5).map((opp) => (
                      <div key={opp.id} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className={`p-2 rounded-full ${PLATFORM_CONFIG[opp.platform]?.color || 'bg-gray-500'}`}>
                          {opp.platform === 'reddit' && <MessageSquare className="h-4 w-4 text-white" />}
                          {opp.platform === 'youtube' && <Youtube className="h-4 w-4 text-white" />}
                          {opp.platform === 'forum' && <MessageCircle className="h-4 w-4 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <a
                              href={opp.conversation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium hover:underline truncate"
                            >
                              {opp.conversation_title}
                            </a>
                            <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {opp.suggested_approach}
                          </p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge variant="secondary">{opp.opportunity_type}</Badge>
                            <Badge className={SENTIMENT_CONFIG[opp.sentiment]?.bgColor}>
                              {opp.sentiment}
                            </Badge>
                            {opp.topics?.slice(0, 2).map((topic, i) => (
                              <Badge key={i} variant="outline">{topic.name}</Badge>
                            ))}
                            {opp.value_tier && (
                              <span className={`text-xs font-bold ${opp.value_tier === '$$$' ? 'text-emerald-600' : opp.value_tier === '$$' ? 'text-emerald-500' : 'text-emerald-400'}`}>
                                {opp.value_tier}
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              Score: {(opp.opportunity_score * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {opportunities.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        No opportunities found. Try harvesting conversations first.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Conversations */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Conversations</CardTitle>
                  <CardDescription>All harvested conversations across categories</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {conversations.slice(0, 10).map((conv) => (
                      <div key={conv.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className={`p-1.5 rounded-full ${PLATFORM_CONFIG[conv.platform]?.color || 'bg-gray-500'}`}>
                          {conv.platform === 'reddit' && <MessageSquare className="h-3.5 w-3.5 text-white" />}
                          {conv.platform === 'youtube' && <Youtube className="h-3.5 w-3.5 text-white" />}
                          {conv.platform === 'forum' && <MessageCircle className="h-3.5 w-3.5 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <a
                              href={conv.conversation_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium hover:underline truncate"
                            >
                              {conv.conversation_title}
                            </a>
                            <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <Badge className={SENTIMENT_CONFIG[conv.sentiment]?.bgColor} variant="secondary">
                              {conv.sentiment}
                            </Badge>
                            {conv.topics?.slice(0, 3).map((topic, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{topic.name}</Badge>
                            ))}
                            {conv.value_tier && (
                              <span className={`text-xs font-bold ${conv.value_tier === '$$$' ? 'text-emerald-600' : conv.value_tier === '$$' ? 'text-emerald-500' : 'text-emerald-400'}`}>
                                {conv.value_tier}
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground ml-auto">
                              {conv.author_username || 'Unknown'} • {conv.upvotes} upvotes
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {conversations.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        No conversations yet. Harvest conversations to get started.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Participation Opportunities</CardTitle>
              <CardDescription>
                Conversations where you can provide value through helpful engagement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {opportunities.map((opp) => (
                  <div key={opp.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${PLATFORM_CONFIG[opp.platform]?.color || 'bg-gray-500'}`}>
                          {opp.platform === 'reddit' && <MessageSquare className="h-5 w-5 text-white" />}
                          {opp.platform === 'youtube' && <Youtube className="h-5 w-5 text-white" />}
                          {opp.platform === 'forum' && <Globe className="h-5 w-5 text-white" />}
                        </div>
                        <div>
                          <a
                            href={opp.conversation_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium hover:underline flex items-center gap-1"
                          >
                            {opp.conversation_title}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          <p className="text-sm text-muted-foreground">
                            by {opp.author_username || 'Unknown'} • {opp.upvotes} upvotes • {opp.comment_count} comments
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        {opp.value_tier && (
                          <div className="text-right">
                            <div className={`text-lg font-bold ${opp.value_tier === '$$$' ? 'text-emerald-600' : opp.value_tier === '$$' ? 'text-emerald-500' : 'text-emerald-400'}`}>
                              {opp.value_tier}
                            </div>
                            <p className="text-xs text-muted-foreground">est. value</p>
                          </div>
                        )}
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {(opp.opportunity_score * 100).toFixed(0)}%
                          </div>
                          <p className="text-xs text-muted-foreground">opportunity score</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm">{opp.conversation_text.substring(0, 300)}...</p>

                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      <p className="text-sm font-medium text-green-700 dark:text-green-300">
                        Suggested Approach:
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        {opp.suggested_approach}
                      </p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge>{opp.opportunity_type}</Badge>
                        <Badge className={SENTIMENT_CONFIG[opp.sentiment]?.bgColor}>
                          {opp.sentiment}
                        </Badge>
                        {opp.topics.slice(0, 3).map((topic, i) => (
                          <Badge key={i} variant="outline">{topic.name}</Badge>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-purple-600 border-purple-200 hover:bg-purple-50 dark:text-purple-400 dark:border-purple-800 dark:hover:bg-purple-950"
                        onClick={() => openDiscoveryForReply(
                          opp.questions?.[0]?.question || opp.conversation_title,
                          opp.platform,
                          opp.conversation_title,
                          opp.conversation_url,
                          opp.conversation_text,
                          opp.suggested_approach,
                          opp.questions?.[0]?.type
                        )}
                      >
                        <Sparkles className="h-4 w-4" />
                        Generate Reply
                      </Button>
                    </div>
                  </div>
                ))}
                {opportunities.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">No opportunities yet</p>
                    <p className="text-muted-foreground">Harvest conversations to find participation opportunities</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sentiment Tab */}
        <TabsContent value="sentiment" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-green-200 dark:border-green-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <ThumbsUp className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Positive</p>
                    <p className="text-2xl font-bold text-green-600">{sentimentSummary?.positivePercent || 0}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Minus className="h-8 w-8 text-gray-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Neutral</p>
                    <p className="text-2xl font-bold text-gray-600">
                      {(100 - parseFloat(sentimentSummary?.positivePercent || '0') - parseFloat(sentimentSummary?.negativePercent || '0')).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 dark:border-red-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <ThumbsDown className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Negative</p>
                    <p className="text-2xl font-bold text-red-600">{sentimentSummary?.negativePercent || 0}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Sentiment Trends</CardTitle>
              <CardDescription>How sentiment has evolved over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={sentimentChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="positive" stroke={SENTIMENT_COLORS.positive} strokeWidth={2} name="Positive" />
                  <Line type="monotone" dataKey="neutral" stroke={SENTIMENT_COLORS.neutral} strokeWidth={2} name="Neutral" />
                  <Line type="monotone" dataKey="negative" stroke={SENTIMENT_COLORS.negative} strokeWidth={2} name="Negative" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Questions Tab */}
        <TabsContent value="questions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Extracted Questions</CardTitle>
              <CardDescription>
                Questions people are asking in online conversations ({extractedQuestions.length} found)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {extractedQuestions.slice(0, 30).map((q, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">{q.question}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          From {q.platform} • {q.upvotes} upvotes
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{q.type}</Badge>
                          {q.answered ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-700">Answered</Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Unanswered</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 truncate">
                          Source: {q.conversationTitle}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-purple-600 border-purple-200 hover:bg-purple-50 dark:text-purple-400 dark:border-purple-800 dark:hover:bg-purple-950"
                          onClick={() => openDiscoveryForReply(
                            q.question,
                            q.platform,
                            q.conversationTitle,
                            q.conversationUrl,
                            undefined, // no full text for questions
                            undefined, // no suggested approach
                            q.type
                          )}
                        >
                          <Sparkles className="h-4 w-4" />
                          Generate Reply
                        </Button>
                        {q.conversationUrl && (
                          <a
                            href={q.conversationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-foreground text-xs flex items-center gap-1"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View source
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {extractedQuestions.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">No questions extracted yet</p>
                    <p className="text-muted-foreground">Harvest and analyze conversations to extract questions</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Entities Tab */}
        <TabsContent value="entities" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Brand Mentions</CardTitle>
                <CardDescription>How brands are being discussed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {entities
                    .filter(e => e.entity_type === 'brand')
                    .slice(0, 10)
                    .map((entity, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => handleEntityClick(entity.entity_name, false)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="font-medium">{entity.entity_name}</div>
                          <Badge className={SENTIMENT_CONFIG[entity.sentiment_toward]?.bgColor}>
                            {entity.sentiment_toward}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="font-bold">{entity.mention_count}</div>
                            <div className="text-xs text-muted-foreground">mentions</div>
                          </div>
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  {entities.filter(e => e.entity_type === 'brand').length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No brand mentions found</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Competitor Mentions</CardTitle>
                <CardDescription>Competitor references in conversations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {entities
                    .filter(e => e.is_competitor)
                    .slice(0, 10)
                    .map((entity, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 border rounded-lg border-orange-200 dark:border-orange-800 hover:bg-orange-50 dark:hover:bg-orange-950/20 cursor-pointer transition-colors"
                        onClick={() => handleEntityClick(entity.entity_name, true)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="font-medium">{entity.entity_name}</div>
                          <Badge className={SENTIMENT_CONFIG[entity.sentiment_toward]?.bgColor}>
                            {entity.sentiment_toward}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="font-bold">{entity.mention_count}</div>
                            <div className="text-xs text-muted-foreground">mentions</div>
                          </div>
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  {entities.filter(e => e.is_competitor).length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No competitor mentions found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Experts Tab */}
        <TabsContent value="experts" className="space-y-6">
          {/* Expert Stats */}
          {expertStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-purple-500" />
                    <div>
                      <p className="text-2xl font-bold">{expertStats.total_experts}</p>
                      <p className="text-xs text-muted-foreground">Total Experts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold">{expertStats.contacted}</p>
                      <p className="text-xs text-muted-foreground">Contacted</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                    <div>
                      <p className="text-2xl font-bold">{expertStats.active_collaborations}</p>
                      <p className="text-xs text-muted-foreground">Active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Star className="h-8 w-8 text-yellow-500" />
                    <div>
                      <p className="text-2xl font-bold">{(expertStats.avg_expertise_score * 100).toFixed(0)}%</p>
                      <p className="text-xs text-muted-foreground">Avg Score</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Pipeline View */}
          {pipeline && (
            <Card>
              <CardHeader>
                <CardTitle>Outreach Pipeline</CardTitle>
                <CardDescription>Track expert outreach progress</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 overflow-x-auto pb-4">
                  {['candidate', 'contacted', 'interested', 'active'].map(status => (
                    <div key={status} className="min-w-[250px] flex-shrink-0">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium capitalize">{status}</h3>
                        <Badge variant="secondary">{pipeline[status]?.count || 0}</Badge>
                      </div>
                      <div className="space-y-2 bg-muted/50 p-3 rounded-lg min-h-[200px]">
                        {(pipeline[status]?.experts || []).slice(0, 5).map(expert => (
                          <div key={expert.id} className="bg-background p-3 rounded border shadow-sm">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${PLATFORM_CONFIG[expert.platform]?.color || 'bg-gray-500'}`} />
                              <span className="font-medium text-sm">{expert.username}</span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-muted-foreground">{expert.platform}</span>
                              <span className="text-xs font-medium">{(expert.expertise_score * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expert List */}
          <Card>
            <CardHeader>
              <CardTitle>Expert Candidates</CardTitle>
              <CardDescription>
                Individuals showing category expertise who could be valuable content partners
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {experts.map((expert) => (
                  <div key={expert.id} className="flex items-start gap-4 p-4 border rounded-lg">
                    <div className={`p-3 rounded-full ${PLATFORM_CONFIG[expert.platform]?.color || 'bg-gray-500'}`}>
                      {expert.platform === 'youtube' ? (
                        <Youtube className="h-5 w-5 text-white" />
                      ) : expert.platform === 'reddit' ? (
                        <MessageSquare className="h-5 w-5 text-white" />
                      ) : (
                        <Globe className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{expert.display_name || expert.username}</span>
                        <Badge className={OUTREACH_STATUS_CONFIG[expert.outreach_status]?.color}>
                          {OUTREACH_STATUS_CONFIG[expert.outreach_status]?.label}
                        </Badge>
                        {expert.verified_status && (
                          <CheckCircle className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">@{expert.username} on {expert.platform}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-medium">{(expert.expertise_score * 100).toFixed(0)}%</span>
                        </div>
                        {expert.follower_count && (
                          <span className="text-sm text-muted-foreground">
                            {expert.follower_count.toLocaleString()} followers
                          </span>
                        )}
                        <span className="text-sm text-muted-foreground">
                          Seen {expert.times_seen}x
                        </span>
                      </div>
                      {expert.expertise_topics.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {expert.expertise_topics.slice(0, 5).map((topic, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{topic}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleExpertClick(expert)}
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        View Profile
                      </Button>
                      {/* Expert linking actions */}
                      {expert.linked_expert_id ? (
                        <>
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                            <Link className="h-3 w-3 mr-1" />
                            {expert.linked_expert_name || 'Linked'}
                          </Badge>
                          <Button
                            variant="default"
                            size="sm"
                            className="bg-violet-600 hover:bg-violet-700"
                            onClick={() => handleStartInterview(expert.linked_expert_id!)}
                          >
                            <Mic className="h-4 w-4 mr-1" />
                            Interview
                          </Button>
                        </>
                      ) : (
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCreateExpertFromVoice(expert)}
                            title="Create PageMind expert profile"
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLinkToExisting(expert)}
                            title="Link to existing expert"
                          >
                            <Link className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {expert.profile_url && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={expert.profile_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Select
                        value={expert.outreach_status}
                        onValueChange={(value) => handleOutreachUpdate(expert.id, value)}
                      >
                        <SelectTrigger className="w-[130px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="candidate">Candidate</SelectItem>
                          <SelectItem value="researching">Researching</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="interested">Interested</SelectItem>
                          <SelectItem value="negotiating">Negotiating</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {experts.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-lg font-medium">No expert candidates yet</p>
                    <p className="text-muted-foreground">
                      Experts are identified from high-quality conversation contributions
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Entity Mentions Sheet */}
      <Sheet open={!!selectedEntity} onOpenChange={() => setSelectedEntity(null)}>
        <SheetContent className="w-[600px] sm:max-w-[600px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {selectedEntity?.isCompetitor && <Target className="h-5 w-5 text-orange-500" />}
              What they're saying about {selectedEntity?.name}
            </SheetTitle>
            <SheetDescription>
              Conversations mentioning this {selectedEntity?.isCompetitor ? 'competitor' : 'brand'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            {loadingEntityMentions ? (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : entityMentions ? (
              <>
                {/* Sentiment Breakdown */}
                <div className="grid grid-cols-4 gap-2 mb-6">
                  <div className="text-center p-2 bg-green-50 dark:bg-green-950 rounded">
                    <div className="text-lg font-bold text-green-600">{entityMentions.sentimentBreakdown.positive}</div>
                    <div className="text-xs text-muted-foreground">Positive</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                    <div className="text-lg font-bold text-gray-600">{entityMentions.sentimentBreakdown.neutral}</div>
                    <div className="text-xs text-muted-foreground">Neutral</div>
                  </div>
                  <div className="text-center p-2 bg-red-50 dark:bg-red-950 rounded">
                    <div className="text-lg font-bold text-red-600">{entityMentions.sentimentBreakdown.negative}</div>
                    <div className="text-xs text-muted-foreground">Negative</div>
                  </div>
                  <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-950 rounded">
                    <div className="text-lg font-bold text-yellow-600">{entityMentions.sentimentBreakdown.mixed}</div>
                    <div className="text-xs text-muted-foreground">Mixed</div>
                  </div>
                </div>

                <ScrollArea className="h-[calc(100vh-280px)]">
                  <div className="space-y-4 pr-4">
                    {entityMentions.mentions.map((mention, i) => (
                      <div key={i} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {mention.platform}
                            </Badge>
                            <Badge className={SENTIMENT_CONFIG[mention.sentiment_toward]?.bgColor}>
                              {mention.sentiment_toward}
                            </Badge>
                          </div>
                          {mention.conversation_url && (
                            <Button variant="ghost" size="sm" asChild>
                              <a href={mention.conversation_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </div>
                        <h4 className="font-medium text-sm mb-1">{mention.conversation_title}</h4>
                        {mention.author_username && (
                          <p className="text-xs text-muted-foreground mb-2">by @{mention.author_username}</p>
                        )}
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {mention.context_snippet || mention.conversation_text?.substring(0, 300)}...
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {mention.upvotes > 0 && (
                            <span className="flex items-center gap-1">
                              <ThumbsUp className="h-3 w-3" /> {mention.upvotes}
                            </span>
                          )}
                          {mention.comment_count > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageCircle className="h-3 w-3" /> {mention.comment_count}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {entityMentions.mentions.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        No conversation details available
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      {/* Expert Profile Sheet */}
      <Sheet open={!!selectedExpert} onOpenChange={() => setSelectedExpert(null)}>
        <SheetContent className="w-[600px] sm:max-w-[600px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <div className={`p-2 rounded-full ${PLATFORM_CONFIG[selectedExpert?.platform || '']?.color || 'bg-gray-500'}`}>
                {selectedExpert?.platform === 'youtube' ? (
                  <Youtube className="h-4 w-4 text-white" />
                ) : selectedExpert?.platform === 'reddit' ? (
                  <MessageSquare className="h-4 w-4 text-white" />
                ) : (
                  <Globe className="h-4 w-4 text-white" />
                )}
              </div>
              {selectedExpert?.display_name || selectedExpert?.username}
            </SheetTitle>
            <SheetDescription>
              @{selectedExpert?.username} on {selectedExpert?.platform}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="h-[calc(100vh-140px)] mt-6">
            <div className="space-y-6 pr-4">
              {loadingExpertProfile ? (
                <div className="space-y-4">
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : (
                <>
                  {/* AI Profile Summary */}
                  {expertProfileSummary && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950 dark:to-blue-950 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        <span className="font-medium">AI Profile Analysis</span>
                        <Badge variant="secondary">{expertProfileSummary.profileType}</Badge>
                      </div>

                      <p className="text-sm mb-4">{expertProfileSummary.summary}</p>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-xs font-medium text-muted-foreground mb-2">Key Strengths</h5>
                          <ul className="space-y-1">
                            {expertProfileSummary.keyStrengths.map((strength, i) => (
                              <li key={i} className="text-xs flex items-start gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h5 className="text-xs font-medium text-muted-foreground mb-2">Collaboration Ideas</h5>
                          <ul className="space-y-1">
                            {expertProfileSummary.collaborationIdeas.map((idea, i) => (
                              <li key={i} className="text-xs flex items-start gap-1">
                                <Star className="h-3 w-3 text-yellow-500 mt-0.5 shrink-0" />
                                {idea}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {expertProfileSummary.outreachTip && (
                        <div className="mt-4 p-2 bg-white/50 dark:bg-black/20 rounded">
                          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                            <Mail className="h-3 w-3" /> Outreach Tip
                          </div>
                          <p className="text-xs">{expertProfileSummary.outreachTip}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expert Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 border rounded-lg">
                      <div className="text-2xl font-bold text-yellow-500">
                        {((selectedExpert?.expertise_score || 0) * 100).toFixed(0)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Expertise Score</div>
                    </div>
                    <div className="text-center p-3 border rounded-lg">
                      <div className="text-2xl font-bold">
                        {selectedExpert?.follower_count?.toLocaleString() || '-'}
                      </div>
                      <div className="text-xs text-muted-foreground">Followers</div>
                    </div>
                    <div className="text-center p-3 border rounded-lg">
                      <div className="text-2xl font-bold">{selectedExpert?.times_seen || 0}</div>
                      <div className="text-xs text-muted-foreground">Times Seen</div>
                    </div>
                  </div>

                  {/* Expertise Topics */}
                  {selectedExpert?.expertise_topics && selectedExpert.expertise_topics.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2">Expertise Topics</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedExpert.expertise_topics.map((topic, i) => (
                          <Badge key={i} variant="outline">{topic}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Recent Conversations */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">Recent Conversations</h4>
                    <div className="space-y-3">
                      {expertConversations.map((conv, i) => (
                        <div key={i} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className="text-xs">{conv.platform}</Badge>
                            {conv.conversation_url && (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={conv.conversation_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </Button>
                            )}
                          </div>
                          <h5 className="text-sm font-medium mb-1">{conv.conversation_title}</h5>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {conv.conversation_text?.substring(0, 200)}...
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            {conv.upvotes > 0 && (
                              <span className="flex items-center gap-1">
                                <ThumbsUp className="h-3 w-3" /> {conv.upvotes}
                              </span>
                            )}
                            {conv.sentiment && (
                              <Badge className={`text-xs ${SENTIMENT_CONFIG[conv.sentiment]?.bgColor}`}>
                                {conv.sentiment}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      {expertConversations.length === 0 && !loadingExpertProfile && (
                        <p className="text-center text-muted-foreground py-4 text-sm">
                          No conversations available
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  {/* PageMind Expert Profile Section */}
                  <div>
                    <h4 className="text-sm font-medium mb-3">PageMind Expert Profile</h4>
                    {selectedExpert?.linked_expert_id ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium">{selectedExpert.linked_expert_name || 'Linked Expert'}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnlinkExpert(selectedExpert.id)}
                            title="Unlink expert"
                          >
                            <Unlink className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </div>
                        <Button
                          className="w-full bg-violet-600 hover:bg-violet-700"
                          onClick={() => handleStartInterview(selectedExpert.linked_expert_id!)}
                        >
                          <Mic className="h-4 w-4 mr-2" />
                          Start Interview with Matched Questions
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground mb-3">
                          Create a PageMind expert profile to start interviews with AI-matched questions.
                        </p>
                        <Button
                          className="w-full"
                          variant="default"
                          onClick={() => handleCreateExpertFromVoice(selectedExpert!)}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Create Expert Profile
                        </Button>
                        <Button
                          className="w-full"
                          variant="outline"
                          onClick={() => handleLinkToExisting(selectedExpert!)}
                        >
                          <Link className="h-4 w-4 mr-2" />
                          Link to Existing Expert
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Profile Link */}
                  {selectedExpert?.profile_url && (
                    <Button className="w-full" variant="outline" asChild>
                      <a href={selectedExpert.profile_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Full Profile on {selectedExpert.platform}
                      </a>
                    </Button>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Create Expert from Voice Dialog */}
      <Sheet open={showCreateExpertDialog} onOpenChange={setShowCreateExpertDialog}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Create Expert Profile
            </SheetTitle>
            <SheetDescription>
              Create a PageMind expert from this discovered voice. You can edit the details before saving.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={createExpertOverrides.name}
                onChange={(e) => setCreateExpertOverrides(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Expert name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input
                value={createExpertOverrides.email}
                onChange={(e) => setCreateExpertOverrides(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email address"
                type="email"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <Input
                value={createExpertOverrides.role}
                onChange={(e) => setCreateExpertOverrides(prev => ({ ...prev, role: e.target.value }))}
                placeholder="e.g., Technical YouTube Reviewer"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Bio</label>
              <Input
                value={createExpertOverrides.bio}
                onChange={(e) => setCreateExpertOverrides(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Leave empty to auto-generate from evidence"
              />
            </div>
            {linkingError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{linkingError}</AlertDescription>
              </Alert>
            )}
            <Button
              className="w-full"
              disabled={!createExpertOverrides.name || creatingExpert}
              onClick={submitCreateExpert}
            >
              {creatingExpert ? 'Creating...' : 'Create & Link Expert'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Link to Existing Expert Dialog */}
      <Sheet open={showLinkExpertDialog} onOpenChange={setShowLinkExpertDialog}>
        <SheetContent className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Link className="h-5 w-5" />
              Link to Existing Expert
            </SheetTitle>
            <SheetDescription>
              Select an existing expert profile to link with this discovered voice.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {linkingError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{linkingError}</AlertDescription>
              </Alert>
            )}
            {loadingMatchingExperts ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : matchingExpertsForLink.length > 0 ? (
              <div className="space-y-2">
                {matchingExpertsForLink.map(expert => (
                  <div
                    key={expert.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => submitLinkExpert(expert.id)}
                  >
                    <div>
                      <p className="font-medium text-sm">{expert.name}</p>
                      {expert.role && <p className="text-xs text-muted-foreground">{expert.role}</p>}
                      {expert.email && <p className="text-xs text-muted-foreground">{expert.email}</p>}
                    </div>
                    <Link className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  No matching experts found for this site. Create a new expert profile instead.
                </p>
                <Button
                  className="mt-4"
                  variant="default"
                  onClick={() => {
                    setShowLinkExpertDialog(false);
                    const voice = experts.find(e => e.id === linkingVoiceId);
                    if (voice) handleCreateExpertFromVoice(voice);
                  }}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Create New Expert
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
